
import { computed, defineComponent, onBeforeMount, ref } from "vue";
import { dispatchError } from "../../../utils/error_handling";
import { wrapLoading } from "../../../utils/wrap_loading";
import { useStore } from "vuex";
import { Notification } from "../../../../../domain/entities/notifications/notification";
import { NotificationType } from "../../../../../domain/enums/notification";
import { useRouter } from "vue-router";
import { dispatchSuccessBox, formatDateTime } from "../../mixins";
import NotificationItem from "./NotificationItem.vue";
import { updateQuery } from "../../../utils/routing_utils";
import ConfirmationModal from "../../modals/ConfirmationModal.vue";
import NotificationModal from "../../modals/NotificationModal.vue";

export default defineComponent({
  name: "NotificationList",
  components: {
    NotificationModal,
    ConfirmationModal,
    NotificationItem
  },
  methods: { formatDateTime },
  setup(_, { emit }) {
    const store = useStore();
    const router = useRouter();

    const isOpened = ref(true);
    const showConfirmation = ref(false);
    const showNotificationModal = ref(false);
    const selectedNotification = ref<Notification | null>(null);
    const notificationToDelete = ref<number | null>(null);
    const confirmationMessage = ref("");
    const confirmationAction = ref<"delete" | "markAll" | null>(null);

    const notifications = computed<Notification[]>(
      () => store.getters["notification_module/notifications"]
    );

    onBeforeMount(async () => {
      await loadNotifications();
    });

    const loadNotifications = async (): Promise<void> => {
      try {
        await wrapLoading(async () => {
          await store.dispatch("notification_module/list");
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    const toggleNotificationModal = async () => {
      isOpened.value = !isOpened.value;
    };

    const setWatchNotification = async (
      notificationID: number
    ): Promise<void> => {
      try {
        await store.dispatch("notification_module/watch", notificationID);
      } catch (e) {
        await dispatchError(e);
      }
    };

    const goToContentView = async (
      courseID: number,
      contentID: number
    ): Promise<void> => {
      await router.push({
        name: "course_content",
        params: {
          course_id: courseID,
          content_id: contentID
        }
      });
      await updateQuery(router, "notification", "true");
    };

    const goToTrainingView = async (courseID: number): Promise<void> => {
      await router.push({
        name: "course",
        params: {
          course_id: courseID
        }
      });
      await updateQuery(router, "notification", "true");
    };

    const goToNotification = async (
      notification: Notification
    ): Promise<void> => {
      const isModalType =
        notification.type === NotificationType.NEW_NOTIFICATION_WITH_IMAGE;

      if (isModalType) {
        emit("closeNotifications");
        await setWatchNotification(notification.id);
        await openNotificationModal(notification.id);
        return;
      }

      emit("closeNotifications");

      switch (notification.type) {
        case NotificationType.DOUBT:
          await goToContentView(
            notification.metadata.idCourse,
            notification.metadata.idContent
          );
          break;
        case NotificationType.INDICATION:
        case NotificationType.NEW_TRAINING:
          await goToTrainingView(notification.metadata.idCourse);
          break;
      }

      await setWatchNotification(notification.id);
    };

    const openConfirmation = (notificationID: number) => {
      notificationToDelete.value = notificationID;
      confirmationMessage.value = "Deseja realmente apagar essa notificação?";
      confirmationAction.value = "delete";
      showConfirmation.value = true;
    };

    const openConfirmationMarkAll = () => {
      confirmationMessage.value =
        "Tem certeza que deseja marcar todas as notificações como lidas?";
      confirmationAction.value = "markAll";
      showConfirmation.value = true;
    };

    const confirmAction = async () => {
      try {
        if (
          confirmationAction.value === "delete" &&
          notificationToDelete.value
        ) {
          await wrapLoading(async () => {
            await store.dispatch(
              "notification_module/delete",
              notificationToDelete.value
            );
            await dispatchSuccessBox("Notificação apagada!");
          });
        }

        if (confirmationAction.value === "markAll") {
          await wrapLoading(async () => {
            if (!notifications || !notifications.value) {
              return;
            }
            for (const notification of notifications.value) {
              if (!notification.isRead) {
                await store.dispatch(
                  "notification_module/watch",
                  notification.id
                );
              }
            }
            await dispatchSuccessBox(
              "Todas as notificações marcadas como lidas!"
            );
          });
        }

        await loadNotifications();
      } catch (e) {
        await dispatchError(e);
      } finally {
        showConfirmation.value = false;
        confirmationAction.value = null;
        notificationToDelete.value = null;
      }
    };

    const closeNotificationModal = () => {
      showNotificationModal.value = false;
    };

    const openNotificationModal = async (id: number) => {
      try {
        await wrapLoading(async () => {
          await loadNotifications();
          await store.dispatch("notification_module/get", id);
          selectedNotification.value =
            store.getters["notification_module/notification"];
          showNotificationModal.value = true;
        });
      } catch (e) {
        await dispatchError(e);
      }
    };

    return {
      notifications,
      isOpened,
      selectedNotification,
      showNotificationModal,
      showConfirmation,
      confirmationMessage,
      openConfirmationMarkAll,
      confirmAction,
      goToNotification,
      toggleNotificationModal,
      closeNotificationModal,
      openNotificationModal,
      openConfirmation
    };
  }
});
